import { isSourcingActive, type SourcingRun } from "./types/sourcing-run";

export type ResetScope = "search" | "filters" | "all";

export const RESET_LABEL: Record<ResetScope, string> = {
  search: "Suche zurücksetzen",
  filters: "Filter zurücksetzen",
  all: "Suche und Filter zurücksetzen",
};

export interface LonglistEmptyInput {
  tab: "longlist" | "selected" | "pool";
  search: string;
  /** Active filters of the filter panel (`activeFilterCount`), search excluded. */
  filterCount: number;
  /** The job's latest search run: null when it has none, undefined when it
   * couldn't be loaded. */
  run: SourcingRun | null | undefined;
  /** Candidates already past the Longlist (Ausgewählt and later). */
  movedOnCount: number;
  isAdmin: boolean;
  jobClosed: boolean;
  /** Whether the job form („Bearbeiten“) is open to changes. */
  jobEditable: boolean;
}

export interface LonglistEmptyState {
  title: string;
  description: string;
  /** What the empty state's reset button clears; absent when there's nothing to reset. */
  reset?: ResetScope;
}

const NO_MATCH: Record<ResetScope, string> = {
  search:
    "Kein Kandidat passt zu deiner Suche. Prüfe die Schreibweise oder suche nach einem Teil des Namens.",
  filters: "Mit den aktuellen Filtern bleibt kein Kandidat übrig.",
  all: "Mit deiner Suche und den Filtern bleibt kein Kandidat übrig.",
};

/** What an empty, loaded candidate list shows (#170). A search or filter the
 * recruiter set is the nearest cause, so it wins over the job's search run;
 * the run's own failure or stop is explained by the banner above the list. */
export function longlistEmptyState(input: LonglistEmptyInput): LonglistEmptyState {
  const searching = input.search.trim() !== "";
  const filtering = input.filterCount > 0;
  if (searching || filtering) {
    const reset: ResetScope = searching && filtering ? "all" : searching ? "search" : "filters";
    return { title: "Keine Treffer", description: NO_MATCH[reset], reset };
  }

  if (input.tab === "selected") {
    return {
      title: "Noch keine Auswahl",
      description: "Markiere in der Longlist, welche Kandidaten weitergehen sollen.",
    };
  }
  if (input.tab === "pool") {
    return {
      title: "Nichts aussortiert",
      description: "Bisher wurde kein gefundener Kandidat aussortiert.",
    };
  }

  const { run } = input;
  if (run && isSourcingActive(run.status)) {
    return {
      title: "Suche läuft",
      description:
        "Neue Kandidaten erscheinen hier, sobald sie bewertet sind. Den Fortschritt siehst du oben.",
    };
  }
  const finished = run?.status === "done" || run?.status === "partial";
  // A restart keeps the selected candidates: one that found nobody says so.
  const foundNobody = finished && run.foundCount === 0;
  if (input.movedOnCount > 0 && !foundNobody) {
    return {
      title: "Longlist abgearbeitet",
      description: "Alle Kandidaten der Longlist sind schon weiter im Prozess.",
    };
  }

  const cause =
    run === null
      ? "Für diese Stelle wurde noch keine Suche gestartet."
      : finished
        ? "Die Suche hat keine passenden Kandidaten gefunden."
        : null;
  // The same search would find nobody again: change it before restarting.
  const restart =
    finished && input.jobEditable
      ? "Passe die Suche über „Bearbeiten“ an und starte sie neu."
      : "Starte die Suche über „Kandidatensuche neu starten“ oben.";
  const nextStep = input.jobClosed
    ? "Die Stelle ist geschlossen."
    : input.isAdmin
      ? restart
      : "Bitte wende dich an das Vidario-Team.";
  return {
    title: "Noch keine Kandidaten",
    description: cause ? `${cause} ${nextStep}` : nextStep,
  };
}
